import React from 'react';

/**
 * JDNextStep Component
 *
 * Per-JD "Next step" chip that shows recommended action.
 * Turns JD list from static to action-oriented.
 *
 * Usage:
 * <JDNextStep
 *   jd={jd}
 *   onAction={(action) => handleAction(action)}
 * />
 */

const getNextStep = (jd) => {
  const { profileCount = 0, matchCount = 0, suggestedCount = 0, shortlistCount = 0, interviewCount = 0 } = jd;

  // Priority-ordered next steps
  if (profileCount === 0) {
    return {
      step: 'upload',
      label: 'Upload Profiles',
      shortLabel: 'Upload CVs',
      description: 'Add candidate profiles to match against this JD',
      variant: 'amber',
      icon: 'upload',
    };
  }

  if (matchCount === 0) {
    return {
      step: 'automatch',
      label: 'Run AutoMatch',
      shortLabel: 'AutoMatch',
      description: `${profileCount} profiles ready for matching`,
      variant: 'blue',
      icon: 'sparkles',
    };
  }

  if (suggestedCount > 0 && shortlistCount === 0) {
    return {
      step: 'review',
      label: 'Review Suggestions',
      shortLabel: 'Review',
      description: `${suggestedCount} candidates suggested`,
      variant: 'green',
      icon: 'check',
    };
  }

  if (shortlistCount > 0 && interviewCount === 0) {
    return {
      step: 'interview',
      label: 'Schedule Interviews',
      shortLabel: 'Schedule',
      description: `${shortlistCount} shortlisted candidates`,
      variant: 'purple',
      icon: 'calendar',
    };
  }

  if (interviewCount > 0) {
    return {
      step: 'progress',
      label: 'In Progress',
      shortLabel: 'Active',
      description: `${interviewCount} interviews scheduled`,
      variant: 'indigo',
      icon: 'progress',
    };
  }

  // Default: JD is complete or idle
  return null;
};

const variantStyles = {
  amber: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    border: 'border-amber-500/25',
    icon: 'text-amber-500',
    hover: 'hover:bg-amber-500/15',
  },
  blue: {
    bg: 'bg-accent-soft',
    text: 'text-blue-400',
    border: 'border-blue-500/25',
    icon: 'text-blue-500',
    hover: 'hover:bg-accent-soft',
  },
  green: {
    bg: 'bg-green-500/10',
    text: 'text-green-400',
    border: 'border-green-500/25',
    icon: 'text-green-500',
    hover: 'hover:bg-green-500/15',
  },
  purple: {
    bg: 'bg-accent-soft',
    text: 'text-blue-400',
    border: 'border-blue-500/25',
    icon: 'text-blue-500',
    hover: 'hover:bg-accent-soft',
  },
  indigo: {
    bg: 'bg-accent-soft',
    text: 'text-blue-400',
    border: 'border-blue-500/25',
    icon: 'text-blue-500',
    hover: 'hover:bg-accent-soft',
  },
  gray: {
    bg: 'bg-muted',
    text: 'text-ink-muted',
    border: 'border-line',
    icon: 'text-ink-subtle',
    hover: 'hover:bg-subtle',
  },
};

const iconMap = {
  upload: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  ),
  sparkles: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  ),
  check: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  calendar: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  progress: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

export const JDNextStep = ({
  jd,
  onAction,
  showDescription = false,
  compact = true,
  clickable = true,
}) => {
  const nextStep = getNextStep(jd);

  if (!nextStep) {
    return null;
  }

  const styles = variantStyles[nextStep.variant] || variantStyles.gray;
  const Icon = iconMap[nextStep.icon];

  const handleClick = (e) => {
    if (clickable && onAction) {
      e.stopPropagation();
      onAction(nextStep.step, jd);
    }
  };

  if (compact) {
    return (
      <button
        onClick={handleClick}
        disabled={!clickable}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${styles.bg} ${styles.text} ${styles.border} ${clickable ? styles.hover + ' cursor-pointer' : 'cursor-default'}`}
        title={nextStep.description}
      >
        <span className={styles.icon}>{Icon}</span>
        <span>{nextStep.shortLabel}</span>
        {clickable && (
          <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={!clickable}
      className={`flex items-center gap-3 px-4 py-2 rounded-lg border transition-colors ${styles.bg} ${styles.text} ${styles.border} ${clickable ? styles.hover + ' cursor-pointer' : 'cursor-default'}`}
    >
      <span className={`p-1.5 rounded-lg ${styles.bg} ${styles.icon}`}>{Icon}</span>
      <div className="text-left">
        <div className="font-medium text-sm">{nextStep.label}</div>
        {showDescription && (
          <div className="text-xs opacity-75">{nextStep.description}</div>
        )}
      </div>
      {clickable && (
        <svg className="w-4 h-4 ml-auto opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </button>
  );
};

/**
 * Helper to determine JD row styling based on state
 */
export const getJDRowState = (jd) => {
  const { profileCount = 0, matchCount = 0, updatedAt, status } = jd;

  // Check if recently active (within last 24 hours)
  const isRecent = updatedAt && (Date.now() - new Date(updatedAt).getTime()) < 24 * 60 * 60 * 1000;

  // Determine state
  if (status === 'closed') {
    return { state: 'closed', className: 'opacity-50' };
  }

  if (matchCount > 0 || isRecent) {
    return { state: 'active', className: 'border-l-2 border-l-blue-500' };
  }

  if (profileCount === 0 && !isRecent) {
    return { state: 'idle', className: 'opacity-75' };
  }

  return { state: 'default', className: '' };
};

export default JDNextStep;
